import { useEffect, useState, useRef } from 'react'
import { useAppStore } from '../store/useAppStore'
import { apiClient } from '../services/apiClient'
import { useAuth } from './useApi'
import { MOCK_INIT_DATA, MOCK_USER, isMockDataAvailable } from '../constants/mockData'
import type { TelegramWebApp } from '../types'
import { useNavigate } from 'react-router-dom'
import { ENV } from '../shared/config/env'

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp
    }
  }
}

// Функция для применения Safe Area Insets как CSS переменных
const applySafeAreaInsets = (webApp: TelegramWebApp) => {
  const root = document.documentElement
  
  // Устанавливаем viewport safe area insets (если доступны)
  if (webApp.safeAreaInset) {
    const { top, bottom, left, right } = webApp.safeAreaInset
    
    root.style.setProperty('--tg-viewport-safe-area-inset-top', `${top}px`)
    root.style.setProperty('--tg-viewport-safe-area-inset-bottom', `${bottom}px`)
    root.style.setProperty('--tg-viewport-safe-area-inset-left', `${left}px`)
    root.style.setProperty('--tg-viewport-safe-area-inset-right', `${right}px`)
  } else {

    root.style.setProperty('--tg-viewport-safe-area-inset-top', '0px')
    root.style.setProperty('--tg-viewport-safe-area-inset-bottom', '0px')
    root.style.setProperty('--tg-viewport-safe-area-inset-left', '0px')
    root.style.setProperty('--tg-viewport-safe-area-inset-right', '0px')
  }
  
  // Устанавливаем content safe area insets (если доступны)
  if (webApp.contentSafeAreaInset) {
    const { top, bottom, left, right } = webApp.contentSafeAreaInset

    root.style.setProperty('--tg-content-safe-area-inset-top', `${top}px`)
    root.style.setProperty('--tg-content-safe-area-inset-bottom', `${bottom}px`)
    root.style.setProperty('--tg-content-safe-area-inset-left', `${left}px`)
    root.style.setProperty('--tg-content-safe-area-inset-right', `${right}px`)
  } else {
 
    root.style.setProperty('--tg-content-safe-area-inset-top', '0px')
    root.style.setProperty('--tg-content-safe-area-inset-bottom', '0px')
    root.style.setProperty('--tg-content-safe-area-inset-left', '0px')
    root.style.setProperty('--tg-content-safe-area-inset-right', '0px')
  }
}

export const useTelegramWebApp = () => {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [startParam, setStartParam] = useState<string | undefined>(undefined)
  const authInitializedRef = useRef(false)
  const backButtonHandlerRef = useRef<(() => void) | null>(null)
  const { setUser, setLoading, authStatus } = useAppStore()
  const authMutation = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    // В web-сборке Telegram SDK отсутствует — хук становится no-op, авторизация
    // идёт через cookie-сессию (AppBootstrap + /user/profile).
    if (ENV.IS_WEB) {
      if (isLoading) setIsLoading(false)
      return
    }
    if (authInitializedRef.current || authStatus === 'pending' || authStatus === 'success') {
      return // Предотвращаем повторную инициализацию
    }
    const initTelegramWebApp = () => {
      const tg = window.Telegram?.WebApp
      setWebApp(tg || null)
      
      if (tg) {
        // Инициализация приложения
        tg.ready()
        
        // Расширяем приложение на весь экран
        tg.expand()
        
        // Определяем платформу и включаем полноэкранный режим только для мобильных устройств
        const platform = tg.platform
        const isMobile = platform === 'android' || platform === 'ios'
        
        if (isMobile && tg.requestFullscreen) {
          tg.requestFullscreen()
        } 
        
        // Отключаем вертикальные свайпы для предотвращения сворачивания (если поддерживается)
        if (tg.disableVerticalSwipes) {
          tg.disableVerticalSwipes()
        }
        
        // Настраиваем кнопку "назад" по умолчанию (но не показываем её)
        const defaultBackHandler = () => {
          navigate(-1)
        }
        backButtonHandlerRef.current = defaultBackHandler
        tg.BackButton.onClick(defaultBackHandler)
        // Не показываем кнопку по умолчанию - каждая страница должна вызывать showBackButton самостоятельно
        
        // Слушаем изменения viewport для обновления safe area insets
        const handleViewportChange = () => {
          setTimeout(() => {
            applySafeAreaInsets(tg)
          }, 100)
        }
        
        // Обработчики событий для отслеживания изменений
        if ('onEvent' in tg && typeof tg.onEvent === 'function') {
          try {
            tg.onEvent('viewportChanged', handleViewportChange)
          } catch (error) {
            console.warn('⚠️ viewportChanged события не поддерживаются:', error)
          }
        }
        
        // Получение данных пользователя и инициализация auth
        const userData = tg.initDataUnsafe?.user
        const initData = tg.initData
        const startParamValue = tg.initDataUnsafe?.start_param // Получаем параметр deep link

        // Сохраняем startParam в state для использования в App.tsx
        setStartParam(startParamValue)
        
        let userToAuth: typeof MOCK_USER
        let initDataToUse: string
        
        if (userData && initData) {
          // Используем реальные данные Telegram
          userToAuth = {
            id: userData.id,
            first_name: userData.first_name,
            last_name: userData.last_name || '',
            username: userData.username || '',
            language_code: userData.language_code || 'ru',
            photo_url: userData.photo_url || ''
          }
          initDataToUse = initData
        } else if (isMockDataAvailable()) {
          // Используем mock данные только если они доступны (в разработке)
          console.warn('⚠️ Нет полных данных от Telegram, используем mock данные')
          userToAuth = MOCK_USER
          initDataToUse = MOCK_INIT_DATA
        } else {
          // В продакшене без данных Telegram - ошибка
          console.error('❌ В продакшене отсутствуют данные от Telegram WebApp')
          throw new Error('Telegram WebApp data is required in production')
        }
        
        // Устанавливаем данные для API клиента
        apiClient.setTelegramInitData(initDataToUse)
        
        // Устанавливаем данные пользователя в store
        setUser({
          id: userToAuth.id,
          first_name: userToAuth.first_name,
          last_name: userToAuth.last_name,
          username: userToAuth.username,
          language_code: userToAuth.language_code,
          photo_url: userToAuth.photo_url
        })
        
        // Выполняем единый auth запрос
        authMutation.mutate({ 
          telegramUserId: userToAuth.id,
          userId: 0
        })
        
        authInitializedRef.current = true
        
        // Настройка внешнего вида
        tg.headerColor = '#7AB5DB'
        tg.backgroundColor = '#7AB5DB'
        try { tg.bottomBarColor = '#ffffff' } catch {}
        try { tg.setBottomBarColor?.('#ffffff') } catch {}
        
        // Применяем safe area insets как CSS переменные
        applySafeAreaInsets(tg)
        
        // Обрабатываем deep link, если есть start_param
        if (startParamValue) {
          console.log('📱 Обнаружен start_param:', startParamValue)
          // Проверяем формат: card_123
          const cardMatch = startParamValue.match(/^card_(\d+)$/)
          if (cardMatch) {
            const cardId = cardMatch[1]
            console.log('🔗 Перенаправление на карточку:', cardId)
            // Небольшая задержка для завершения инициализации
            setTimeout(() => {
              navigate(`/card/${cardId}`)
            }, 500)
          }
          // Проверяем формат: wheel - для открытия колеса фортуны
          else if (startParamValue === 'wheel') {
            console.log('🎡 Перенаправление на колесо фортуны')
            // Небольшая задержка для завершения инициализации
            setTimeout(() => {
              navigate('/fortune-wheel')
            }, 500)
          }
        }
        
        setIsLoading(false)
        setLoading(false)

        // Очистка при размонтировании
        return () => {
          if (backButtonHandlerRef.current && tg.BackButton.offClick) {
            tg.BackButton.offClick(backButtonHandlerRef.current)
          }
          tg.BackButton.hide()
        }
      } else if (isMockDataAvailable()) {
        // Режим разработки без Telegram WebApp
        console.warn('⚠️ Telegram WebApp не найден, используем mock данные для разработки')
        
        // Устанавливаем mock данные
        apiClient.setTelegramInitData(MOCK_INIT_DATA)
        setUser(MOCK_USER)
        
        // Выполняем единый auth запрос с mock данными
        authMutation.mutate({ 
          telegramUserId: MOCK_USER.id,
          userId: 0
        })
        
        authInitializedRef.current = true
        setIsLoading(false)
        setLoading(false)
      } else {
        // В продакшене без Telegram WebApp - ошибка
        console.error('❌ Telegram WebApp не найден в продакшене')
        throw new Error('Telegram WebApp is required in production')
      }
    }

    initTelegramWebApp()
  }, []) // Выполняем только один раз при монтировании



  const showMainButton = (text: string, onClick: () => void) => {
    if (webApp?.MainButton) {
      webApp.MainButton.setText(text)
      webApp.MainButton.onClick(onClick)
      webApp.MainButton.show()
    }
  }

  const hideMainButton = () => {
    if (webApp?.MainButton) {
      webApp.MainButton.hide()
    }
  }

  const showBackButton = (onClick: () => void) => {
    if (webApp?.BackButton) {
      // Удаляем предыдущий обработчик, если он был
      if (backButtonHandlerRef.current && webApp.BackButton.offClick) {
        webApp.BackButton.offClick(backButtonHandlerRef.current)
      }

      // Сохраняем новый обработчик
      backButtonHandlerRef.current = onClick

      // Устанавливаем новый обработчик
      webApp.BackButton.onClick(onClick)
      webApp.BackButton.show()
    }
  }

  const hideBackButton = () => {
    if (webApp?.BackButton) {
      // Удаляем текущий обработчик, если он есть
      if (backButtonHandlerRef.current && webApp.BackButton.offClick) {
        webApp.BackButton.offClick(backButtonHandlerRef.current)
        backButtonHandlerRef.current = null
      }
      webApp.BackButton.hide()
    }
  }

  const hapticFeedback = (type: 'light' | 'medium' | 'heavy' | 'success' | 'error') => {
    if (webApp?.HapticFeedback) {
      if (['success', 'error'].includes(type)) {
        webApp.HapticFeedback.notificationOccurred(type as 'success' | 'error')
      } else {
        webApp.HapticFeedback.impactOccurred(type as 'light' | 'medium' | 'heavy')
      }
    }
  }

  const closeMiniApp = () => {
    if (webApp) {
      webApp.close()
    }
  }

  const requestFullscreen = () => {
    if (webApp?.requestFullscreen) {
      try {
        webApp.requestFullscreen()
      } catch (error) {
        console.warn('⚠️ requestFullscreen не поддерживается:', error)
        return false
      }
      return true
    }
    console.warn('⚠️ requestFullscreen не доступен в WebApp')
    return false
  }

  const toggleVerticalSwipes = (enabled: boolean) => {
    if (webApp) {
      if (enabled && webApp.enableVerticalSwipes) {
        webApp.enableVerticalSwipes()
      } else if (!enabled && webApp.disableVerticalSwipes) {
        webApp.disableVerticalSwipes()
      }
    }
  }

  const getSafeAreaInsets = () => {
    // Получаем viewport safe area insets
    const viewportInsets = webApp?.safeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 }
    
    // Получаем content safe area insets
    const contentInsets = webApp?.contentSafeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 }
    
    // Если webApp доступен, используем данные оттуда
    if (webApp) {
      return {
        top: viewportInsets.top + contentInsets.top,
        bottom: viewportInsets.bottom + contentInsets.bottom,
        left: viewportInsets.left + contentInsets.left,
        right: viewportInsets.right + contentInsets.right
      }
    }
    
    // Если webApp не доступен, пытаемся получить из CSS переменных и складываем их
    const root = document.documentElement
    const computedStyle = getComputedStyle(root)
    
    const getCSSValue = (varName: string): number => {
      const value = computedStyle.getPropertyValue(varName).trim()
      return parseInt(value) || 0
    }
    
    return {
      top: getCSSValue('--tg-viewport-safe-area-inset-top') + getCSSValue('--tg-content-safe-area-inset-top'),
      bottom: getCSSValue('--tg-viewport-safe-area-inset-bottom') + getCSSValue('--tg-content-safe-area-inset-bottom'),
      left: getCSSValue('--tg-viewport-safe-area-inset-left') + getCSSValue('--tg-content-safe-area-inset-left'),
      right: getCSSValue('--tg-viewport-safe-area-inset-right') + getCSSValue('--tg-content-safe-area-inset-right')
    }
  }

  const updateSafeAreaInsets = () => {
    if (webApp) {
      applySafeAreaInsets(webApp)
    }
  }

  // Информация о платформе
  const platform = webApp?.platform || 'unknown'
  const isMobile = platform === 'android' || platform === 'ios'
  const isAndroid = platform === 'android'
  const isIOS = platform === 'ios'

  return {
    webApp,
    isLoading,
    startParam,
    showMainButton,
    hideMainButton,
    showBackButton,
    hideBackButton,
    hapticFeedback,
    closeMiniApp,
    requestFullscreen,
    toggleVerticalSwipes,
    getSafeAreaInsets,
    updateSafeAreaInsets,
    platform,
    isMobile,
    isAndroid,
    isIOS,
    version: webApp?.version || 'unknown',
    colorScheme: webApp?.colorScheme || 'light'
  }
} 